package com.appsyde.shared.app.presentation.ticket

import com.appsyde.shared.modules.ai.domain.model.DetectedFieldV2
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

/**
 * Parse ticket memory JSON and enrich with client email when missing.
 * Used to build sidebar "detected" fields from ticket-level data.
 */
object TicketDetectedFromMemory {

    private val json = Json { ignoreUnknownKeys = true }

    fun parseAndEnrich(rawJson: String?, clientEmail: String?): List<DetectedFieldV2> {
        val base = parseMemoryJsonToDetectedFields(rawJson)
        return enrichWithClientEmail(base, clientEmail)
    }

    fun parseMemoryJsonToDetectedFields(rawJson: String?): List<DetectedFieldV2> {
        val raw = rawJson?.trim().takeIf { !it.isNullOrBlank() } ?: return emptyList()
        val normalized = raw.replace("\"\"", "\"")
        val obj = runCatching { json.parseToJsonElement(normalized).jsonObject }.getOrNull()
            ?: return emptyList()
        return obj.entries.mapNotNull { (k, v) ->
            val key = k.trim()
            val value = runCatching { v.jsonPrimitive.content.trim() }.getOrNull().orEmpty()
            if (key.isBlank() || value.isBlank()) null
            else DetectedFieldV2(key = key, label = null, value = value, status = "detected")
        }
    }

    fun enrichWithClientEmail(
        base: List<DetectedFieldV2>,
        clientEmail: String?
    ): List<DetectedFieldV2> {
        val email = clientEmail?.trim()?.takeIf { it.isNotBlank() } ?: return base
        val hasEmail = base.any {
            val k = it.key?.trim()?.lowercase().orEmpty()
            k == "customer_email" || k == "email"
        }
        if (hasEmail) return base
        return base + DetectedFieldV2(
            key = "customer_email",
            label = null,
            value = email,
            status = "detected"
        )
    }

    /**
     * Parse detected_sources_json (key -> { source, updated_at }) to key -> source for UI badges.
     * Source values: "ai" | "agent_entered" | "agent_confirmed".
     */
    fun parseDetectedSourcesJson(sourcesJson: String?): Map<String, String> {
        if (sourcesJson.isNullOrBlank()) return emptyMap()
        val obj = runCatching { json.parseToJsonElement(sourcesJson.trim()).jsonObject }.getOrNull() ?: return emptyMap()
        return obj.mapNotNull { (k, v) ->
            val key = k.trim().lowercase().takeIf { it.isNotBlank() } ?: return@mapNotNull null
            val meta = v as? JsonObject ?: return@mapNotNull null
            val source = runCatching { meta["source"]?.jsonPrimitive?.content?.trim() }.getOrNull().takeIf { !it.isNullOrBlank() } ?: return@mapNotNull null
            key to source.lowercase()
        }.toMap()
    }
}
